import { useCallback } from 'react';
import { useUpsertAllowedCustomersMutation } from '../queries/upsertAllowedCustomers.mutation.generated';
import { useRemoveAllowedCustomerMutation } from '../queries/removeAllowedCustomer.mutation.generated';
import { GetUserDetailsDocument } from '../queries/getUserDetails.query.generated';
import { useStore } from '@store';

function stamp() {
  const now = new Date().toISOString();
  const email = useStore.getState().user?.profile?.email || 'portal';
  return { created_by: email, updated_by: email, created_at: now, updated_at: now };
}

/**
 * Centralized hook for managing user-to-customer (market) links.
 */
export function useMarketAccess() {
  const [upsertCustomers] = useUpsertAllowedCustomersMutation();
  const [removeCustomer] = useRemoveAllowedCustomerMutation();

  /** Grant a user access to one or more markets. */
  const grantMarkets = useCallback(
    async (userId: number, customerIds: number[]) => {
      if (customerIds.length === 0) return;
      const audit = stamp();
      await upsertCustomers({
        variables: {
          allowedCustomersObj: customerIds.map(cid => ({
            user_id: userId,
            customer_id: cid,
            ...audit,
          })),
        },
        refetchQueries: [{ query: GetUserDetailsDocument, variables: { userId } }],
      });
    },
    [upsertCustomers],
  );

  /** Grant a user access to a single market. */
  const grantMarket = useCallback(
    async (userId: number, customerId: number) => grantMarkets(userId, [customerId]),
    [grantMarkets],
  );

  /** Remove a user's access to a single market. */
  const revokeMarket = useCallback(
    async (userId: number, customerId: number) => {
      await removeCustomer({
        variables: { userId, customerId },
        refetchQueries: [{ query: GetUserDetailsDocument, variables: { userId } }],
      });
    },
    [removeCustomer],
  );

  return { grantMarkets, grantMarket, revokeMarket };
}
